import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useTranslation } from 'react-i18next'

import PageSection from '../../../../features/components/PageSection'
import Button from '../../../../components/Button'
import InternalLink from '../../../../components/InternalLink'
import BoostOrganicPost from '../../../../forms/Multiplatform/Partnerships/BoostOrganicPost'
import ShortDigitalRightCreate from '../../../../forms/Multiplatform/Partnerships/RightForms/SpecificRightForms/ShortDigitalRightCreate'

import { generateLabelsText, getFormattedProviders } from './helpers'
import { showToasts } from '../../../../helpers/toasts'
import { getPartnerbrainName } from '../../../../features/helpers/getAppDomainName'

import { openForm } from '../../../../modules/actions/forms'
import { selectedPartnershipSelector } from '../../../../modules/selectors/partnerships'

import { PROVIDER_TO_PLATFORMS } from '../../../../constants/selectLists/platformList'
import { ROUTES } from '../../../../constants/routes'
import { PARTNERSHIP_EDIT } from '../../../../constants/forms'
import { TOAST_TYPE } from '../../../../constants/other'

import useStyles from '../styles'

const CampaignsSection = ({ goToCampaignHubButtonHandler }) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const partnerbrainName = getPartnerbrainName()

  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const { providers = [], id: selectedPartnershipId } = selectedPartnership

  const formattedProviders = getFormattedProviders(providers)
  const formattedMissedProviders = getFormattedProviders(
    Object.keys(PROVIDER_TO_PLATFORMS).filter(provider => !providers.includes(provider))
  )

  const providersText = generateLabelsText(formattedProviders)
  const missedProvidersText = generateLabelsText(formattedMissedProviders, 'or')

  const onAddAdAccountLinkClick = () => {
    dispatch(openForm({ id: selectedPartnershipId, formName: PARTNERSHIP_EDIT }))
  }

  const onEmptyProvidersCreateCampaignButtonClick = () => {
    showToasts({
      type: TOAST_TYPE.info,
      message: "You haven't added any ad accounts yet, please add one on the partnerships settings page.."
    })
  }

  return (
    <PageSection className={classes.section}>
      <h3 className={classes.sectionTitle}>Launch your own campaigns</h3>
      <div className={classnames(classes.subSectionsContainer, classes.campaignsSubSectionContainer)}>
        <div className={classnames(classes.subSection, classes.multiPlatformCampaignSubSection)}>
          <h4 className={classes.sectionSubTitle}>Option 1: Create a multi-platform campaign with optimisation</h4>
          <p>
            {providersText && `Create campaigns on ${providersText}.`}
            {missedProvidersText && (
              <>
                {` To add ${missedProvidersText}
                accounts `}
                <InternalLink
                  onClick={onAddAdAccountLinkClick}
                  to={ROUTES.settingsPartnerships}
                  className={classes.link}
                >
                  click here
                </InternalLink>
                .
              </>
            )}
          </p>
          <br />
          <p>{t('partnerbrainCreateCampaigns', { partnerbrainName })}</p>
          {providers.length ? (
            <ShortDigitalRightCreate openBtnClassName={classes.actionButton} />
          ) : (
            <Button onClick={onEmptyProvidersCreateCampaignButtonClick} className={classes.actionButton}>
              Create multi-platform campaign
            </Button>
          )}
        </div>
        <div className={classes.subSection}>
          <h4 className={classes.sectionSubTitle}>Option 2: Use advanced campaign creation form</h4>
          <p>Create campaigns per-platform using the full set of options.</p>
          <Button onClick={goToCampaignHubButtonHandler} className={classes.actionButton}>
            Go to campaign hub
          </Button>
        </div>
        <div className={classes.subSection}>
          <h4 className={classes.sectionSubTitle}>Option 3: Boost an organic post</h4>
          <p>Boost posts on your Facebook or Instagram pages to your fan audiences.</p>
          <BoostOrganicPost openBtnClassName={classes.actionButton} />
        </div>
      </div>
    </PageSection>
  )
}

CampaignsSection.propTypes = {
  goToCampaignHubButtonHandler: PropTypes.func.isRequired
}

export default CampaignsSection
